import sumBy from 'lodash/sumBy';
import { store } from '../../store';
import { updatePaymentSettings } from '../../actions/payments';
import { MAIL, PICKUP } from '../../constants/returnMethods';
import { setShowAddressOnStep3, updateStep2Complete } from '../../actions/page';
import { returnItemsService } from '../return-items-service/return-items-service';
import { updateEnableEmail } from '../../actions/returnConfirmation';
import { selectReturnMethod } from '../../actions/returnMethods';
import { retailerSettingsService } from '../retailer-settings/retailer-settings';
import { setDefaultOptions } from '../../actions/options';
import { setExpressUpgradeFee } from '../../actions/repairs';
import { receiveRefundEligiblity } from '../../actions/refunds';

const returnFeeFunction = () => {
  const {
    options: { selectedOptions },
  } = store.getState();
  const shipTerms = {};
  let returnSubtotal = null;
  let selectedMethod = {};
  let estimatedRefund = 0;
  let handlingFee;
  let creditReversalAmount;
  let recyclingFeeReversal;
  let netCost;
  let discountReversal;
  let nonMonetaryAttributes;
  let returnCost;
  let returnCredit;
  let restockingFee;
  let returnTax;

  const getHandlingFee = () => handlingFee;
  const getCreditReversalAmount = () => creditReversalAmount;
  const getRecyclingFeeReversal = () => recyclingFeeReversal;
  const getNetCost = () => netCost;
  const getDiscountReversal = () => discountReversal;
  const getNonMonetaryAttributes = () => nonMonetaryAttributes;
  const getTerms = () => shipTerms;
  const getReturnSubtotal = () => returnSubtotal;
  const getReturnCost = () => returnCost;
  const getReturnCredit = () => returnCredit;
  const getRestockingFee = () => restockingFee;
  const getReturnTax = () => returnTax;

  const setReturnSubtotal = (subtotal) => {
    returnSubtotal = subtotal;
  };

  const calculateRepairFees = (items, shippingFee = 0) => {
    const expressUpgrade = selectedMethod.express_upgrade_option;
    const expressUpgradeData = selectedOptions.express_upgrade;
    const itemTotal = sumBy(items, 'repair_reason_fee');

    const expressUpgradeFee =
      expressUpgrade && expressUpgradeData ? parseFloat(expressUpgradeData.value) : 0;

    const finalTotal = itemTotal + shippingFee + expressUpgradeFee + handlingFee;

    return { itemTotal, shippingFee, expressUpgradeFee, finalTotal };
  };

  const getPaymentConfiguration = (method) => {
    const payments = method?.payments || {};
    store.dispatch(updatePaymentSettings(payments));
  };

  const setSelectedMethod = (value) => {
    selectedMethod = value;
  };
  const getSelectedMethod = () => selectedMethod;

  const getItemTotal = () =>
    sumBy(returnItemsService.getReturnOrder()?.return_items, 'total_price');

  const getCalculatedTotal = () => {
    const {
      page: { productType },
      returnMethods: { selectedMethod: method } = {},
      returnsSettings: {
        [productType]: {
          refunds: {
            selectedType,
            itemizationsByType: {
              [selectedType]: { estimated_refund: { value: estimatesApiRefundAmount } = {} } = {},
            } = {},
            enable_api: refundApiEnabled = false,
          } = {},
        } = {},
      },
    } = store.getState();

    const { return_cost: returnCosts = {}, payments: { enabled: paymentEnabled = false } = {} } =
      method;

    restockingFee = returnCosts.return_restocking_fee || 0;
    returnCost = paymentEnabled ? 0 : returnCosts.return_shipping_fee || 0;
    returnCredit = returnCosts.return_shipping_credit || 0;
    returnTax = returnCosts.return_tax_credit || 0;

    const itemTotal = getItemTotal();

    let totalFees =
      restockingFee +
      handlingFee +
      (paymentEnabled ? 0 : returnCost) +
      netCost +
      discountReversal +
      creditReversalAmount;

    totalFees -= returnCredit;

    return refundApiEnabled && selectedType !== ''
      ? estimatesApiRefundAmount
      : itemTotal - totalFees + returnTax + recyclingFeeReversal;
  };

  const setEstimatedRefund = (val) => {
    // TODO: Move this to redux
    estimatedRefund = val;
  };

  const getEstimatedRefund = () => estimatedRefund;

  const calculateRefund = (callback) => {
    const {
      addresses: { isShippingAddressValid },
      page: { productType },
      returnsSettings: {
        [productType]: {
          refunds: { allow_only_positive_refunds: allowOnlyPositiveRefunds = false } = {},
          returnOptions: { enabled: returnOptionsEnabled = false } = {},
        } = {},
      } = {},
      refunds: { selectedType } = {},
    } = store.getState();
    const {
      return_cost: returnCosts = {},
      attributes,
      payments: { enabled: paymentEnabled = false } = {},
    } = selectedMethod;
    const isExchangesOnly = returnItemsService.isExchangesOnly();
    const product = retailerSettingsService.getProduct();
    const returnItems = returnItemsService.getItems();
    handlingFee = returnCosts.return_handling_fee || 0;
    creditReversalAmount = returnCosts.credit_reversal_amount || 0;
    recyclingFeeReversal = returnCosts.recycling_fee_reversal || 0;

    if (product === 'returns') {
      const itemTotal = getItemTotal();
      setReturnSubtotal(itemTotal);
      returnCost = paymentEnabled ? 0 : returnCosts.return_shipping_fee || 0;
      returnCredit = returnCosts.return_shipping_credit || 0;
      restockingFee = returnCosts.return_restocking_fee || 0;
      returnTax = returnCosts.return_tax_credit || 0;
      netCost = returnCosts.return_net_cost || 0;
      discountReversal = returnCosts.discounts_reversal || 0;
      nonMonetaryAttributes = attributes;

      const calculatedTotal = getCalculatedTotal();

      if (calculatedTotal > 0 || selectedType === '') {
        if (allowOnlyPositiveRefunds) {
          setEstimatedRefund(calculatedTotal >= 0 ? calculatedTotal : 0);
          if (getEstimatedRefund() > 0) {
            retailerSettingsService.setRequiredConditions('positive-refund', true);
          } else {
            retailerSettingsService.setRequiredConditions('positive-refund', false);
          }
        } else {
          setEstimatedRefund(calculatedTotal);
        }
        store.dispatch(receiveRefundEligiblity(true));
      } else {
        setEstimatedRefund(0);
        if (allowOnlyPositiveRefunds && !isExchangesOnly) {
          store.dispatch(receiveRefundEligiblity(false));
        }
      }
    } else {
      const shippingFee = returnCosts.return_shipping_fee;
      const repairFeeData = calculateRepairFees(returnItems, shippingFee);
      const { itemTotal, expressUpgradeFee, finalTotal } = repairFeeData;
      returnCost = shippingFee;
      setEstimatedRefund(finalTotal > 0 ? finalTotal : 0);
      store.dispatch(setExpressUpgradeFee(expressUpgradeFee || undefined));
      setReturnSubtotal(itemTotal);
    }
    // For Legacy UI Shipping Address sidebar use only
    if (isShippingAddressValid && !returnOptionsEnabled) {
      callback();
    }
  };

  const getFee = (method, toStep3) => {
    if (!method) return;
    getPaymentConfiguration(method);

    const {
      addresses: { shippingAddress } = {},
      carrierPickup: { date: pickupDate } = {},
      returnMethods: { returnMethods } = {},
      page: { productType, locale, giftOrder = false } = {},
      returnOptions: { selectedOptionId } = {},
      returnsSettings: {
        [productType]: {
          sidebarShippingMethodConfig,
          disableGiftAddressForm = false,
          returnOptions: {
            enabled: returnOptionsEnabled = false,
            methods: returnOptionsMethods = {},
          } = {},
        } = {},
      } = {},
    } = store.getState();
    const collectGiftAddress = giftOrder && locale === 'en_US' && !disableGiftAddressForm;
    const hasSidebarAddressConfig =
      sidebarShippingMethodConfig && sidebarShippingMethodConfig.length > 0;
    const hasFormAddressConfig =
      returnOptionsEnabled &&
      Object.values(returnOptionsMethods).some(
        ({ get_address: getAddress }) => getAddress === true && selectedOptionId !== PICKUP,
      );
    const useGiftAddressSidebar = giftOrder && locale === 'en_US' && !returnOptionsEnabled;
    const isShippingAddressEmpty = Object.keys(shippingAddress).length === 0;

    if (method?.id === PICKUP) {
      store.dispatch(updateStep2Complete(Boolean(pickupDate)));
    } else {
      store.dispatch(updateStep2Complete(returnItemsService.getTotalSelected() > 0));
    }

    selectedMethod = method;

    // Deselect all and select this
    returnMethods.forEach((returnMethod) => {
      returnMethod.default = false;
    });

    method.default = true;
    store.dispatch(updateEnableEmail(!method.disable_email));
    returnItemsService.updateMethod(method.id);

    const order = returnItemsService.getReturnOrder();
    order.shipping_method = method.shipping_method;

    store.dispatch(
      selectReturnMethod({
        selectedMethod: method,
        selectedMethodKey: method.carrier ? `${method.id}_${method.carrier}` : method.id,
      }),
    );

    const showAddressOnStep3 =
      (method.id === MAIL && returnItemsService.hasAnExchange()) ||
      method.get_address ||
      collectGiftAddress;
    store.dispatch(setShowAddressOnStep3(showAddressOnStep3));

    retailerSettingsService.setShowReturnAddress(showAddressOnStep3);

    if (
      (((hasSidebarAddressConfig || hasFormAddressConfig) && selectedMethod.get_address) ||
        useGiftAddressSidebar) &&
      isShippingAddressEmpty
    ) {
      store.dispatch(updateStep2Complete(false));
    }

    if (selectedMethod.express_upgrade_option) {
      store.dispatch(setDefaultOptions(selectedMethod.express_upgrade_option));
    }

    calculateRefund(toStep3);
  };

  return {
    getTerms,
    getReturnSubtotal,
    setReturnSubtotal,
    calculateRepairFees,
    getFee,
    getSelectedMethod,
    setSelectedMethod,
    getCalculatedTotal,
    getItemTotal,
    setEstimatedRefund,
    getEstimatedRefund,
    getHandlingFee,
    getCreditReversalAmount,
    getRecyclingFeeReversal,
    getNetCost,
    getDiscountReversal,
    getNonMonetaryAttributes,
    getReturnCost,
    getReturnCredit,
    getRestockingFee,
    getReturnTax,
    calculateRefund,
  };
};

export const returnFeeService = returnFeeFunction();
